import { Input, Skeleton, useColorModeValue, useToast } from '@chakra-ui/react';
import { motion, useAnimation } from 'framer-motion';
import { t } from 'i18next';
import { isEmpty } from 'lodash';
import { forwardRef, useEffect, useImperativeHandle, useState } from 'react';
import { Trans } from 'react-i18next';
import { useQueryClient } from 'react-query';
import styled from 'styled-components';
import LightColors from '../../constants/LightColors';
import { FAN_BUDDY_MBTI } from '../../utils/FanBuddyHelper';
import { isDarkMode } from '../../utils/ThemeHelper';
import ToastMessageBox from '../alerts/ToastMessageBox';
import UpdateInvitationCode from '../api/mutations/UpdateInvitationCode';
import useBuddyPreferenceQuery from '../api/queries/useBuddyPreferenceQuery';
import useUserQuery from '../api/queries/useUserQuery';
import PrimaryButton from '../buttons/PrimaryButton';

const CardContainer = styled.div`
  width: 100%;
  height: 589px;
  padding: 20px 24px 24px 24px;
  display: flex;
  border-radius: 18px;
  flex-direction: column;
  background: ${(props) =>
    props.theme.isDark
      ? `
        linear-gradient(
          145deg,
          rgba(34, 34, 34, 0.8) 0%,
          rgba(34, 34, 34, 0.9) 5.61%,
          rgba(34, 34, 34, 0.3) 19.79%,
          rgba(34, 34, 34, 0.9) 36%,
          rgba(34, 34, 34, 0.5) 70%,
          rgba(34, 34, 34, 0.9) 100%
        ),
        linear-gradient(
          205deg,
          rgba(${props.backgroundPrimaryColor}, 0.7) 0%,
          rgba(34, 34, 34, 0.2) 41.2%,
          rgba(${props.backgroundSecondaryColor}, 0.7) 82.4%
        )
      `
      : `
        linear-gradient(
          158deg,
          rgba(255, 255, 255, 0.8) 0%,
          rgba(255, 255, 255, 0.9) 5.61%,
          rgba(255, 255, 255, 0.3) 19.79%,
          rgba(255, 255, 255, 0.9) 36%,
          rgba(255, 255, 255, 0.5) 70%,
          rgba(255, 255, 255, 0.9) 100%
        ),
        linear-gradient(
          205deg,
          rgba(${props.backgroundPrimaryColor}) 0%,
          rgba(255, 255, 255, 0.2) 41.2%,
          rgba(${props.backgroundSecondaryColor}) 82.4%
        )
      `};
  box-shadow: ${(props) =>
    props.theme.isDark
      ? `
        0px -6px 40px 0px rgba(0,0,0,0.2),
        0px 8px 16px rgba(0,0,0,0.1)
      `
      : `
        0px -6px 40px 0px rgba(${props.backgroundPrimaryColor}, 0.2),
        0px 8px 16px rgba(0, 0, 0, 0.05)
      `};
  backdrop-filter: blur(8px);
`;

function Title({ children, className = '', ...props }) {
  return (
    <div
      className={`font-semibold text-[17px] leading-[20px] text-center break-keep whitespace-pre-wrap text-main-light-1 dark:text-main-dark-1 ${className}`}
      {...props}
    >
      {children}
    </div>
  );
}

function Description({ children, className = '', ...props }) {
  return (
    <div
      className={`font-normal text-[14px] leading-[17px] text-center break-keep text-main-light-4 dark:text-main-dark-4 ${className}`}
      {...props}
    >
      {children}
    </div>
  );
}

const InviteFanBuddySwipeCard = forwardRef((props, ref) => {
  const controls = useAnimation();
  const queryClient = useQueryClient();

  // useEffect(() => {
  //   queryClient.invalidateQueries('MyBuddyPreferenceQuery');
  // }, [queryClient]);

  const user = useUserQuery();
  const currentUserbuddyUserProfile = useBuddyPreferenceQuery();

  useImperativeHandle(ref, () => ({
    triggerSwipeAnimation: async (direction) => {
      await controls.start({
        x: direction === 'right' ? 500 : -500,
        opacity: 0,
        rotate: direction === 'right' ? 50 : -50,
        transition: { duration: 0.3 }
      });
      controls.set({ x: 0, opacity: 1, rotate: 0 });
    }
  }));

  const isUserNeedingInviteCode = () => {
    return false;
    // return isEmpty(user?.inviterId) && new Date(user?.verifiedAt) > new Date('2025-03-05');
  };

  if (isEmpty(currentUserbuddyUserProfile) || isEmpty(user)) {
    return <></>;
  }

  return (
    <motion.div animate={controls} style={{ cursor: 'default' }}>
      <CardContainer
        initial={{ scale: 0.75, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.9, opacity: 0 }}
        transition={{ duration: 0.3 }}
        backgroundPrimaryColor={
          FAN_BUDDY_MBTI[currentUserbuddyUserProfile.mbti]?.backgroundColor?.primary
        }
        backgroundSecondaryColor={
          FAN_BUDDY_MBTI[currentUserbuddyUserProfile.mbti]?.backgroundColor?.secondary
        }
        isDarkMode={isDarkMode()}
      >
        <SkeletonMutualMembersSection />
        <SkeletonProfileSection />

        {isUserNeedingInviteCode() ? (
          <>
            <UpdateInvitationCodeSection
              resetSwipeAt={currentUserbuddyUserProfile?.resetSwipeAt}
            />
          </>
        ) : (
          // <>
          //   <CopyInvitationCodeSection
          //     invitationCode={user.invitationCode}
          //     resetSwipeAt={currentUserbuddyUserProfile?.resetSwipeAt}
          //   />
          //   <InvitationDesclaimerSection />
          // </>
          <>
            <NotAvailbleSwipeSection resetSwipeAt={currentUserbuddyUserProfile?.resetSwipeAt} />
          </>
        )}
      </CardContainer>
    </motion.div>
  );
});

export default InviteFanBuddySwipeCard;

const SkeletonMutualMembersSection = () => {
  const startColor = useColorModeValue('separator.light.1', 'separator.dark.1');
  const endColor = useColorModeValue('separator.light.2', 'separator.dark.2');

  return (
    <div className="w-full flex justify-between place-items-center mb-[32px]">
      <div className="flex flex-row gap-[5px] place-items-center">
        <img src="/assets/icons/like.shape.shine.png" alt="mutual" width={20} height={20} />
        <div>
          <Skeleton
            width={'145px'}
            height={'12px'}
            startColor={startColor}
            endColor={endColor}
            borderRadius={'12px'}
          />
        </div>
      </div>
    </div>
  );
};

const SkeletonProfileSection = () => {
  const startColor = useColorModeValue('separator.light.1', 'separator.dark.1');
  const endColor = useColorModeValue('separator.light.2', 'separator.dark.2');

  return (
    <div className={'flex justify-between w-full mb-[104px]'}>
      <div className="flex flex-col gap-2 self-center">
        <div className="flex flex-row">
          <Skeleton
            width={'77px'}
            height={'17px'}
            startColor={startColor}
            endColor={endColor}
            borderRadius={'12px'}
          />
        </div>
        <Skeleton
          width={'126px'}
          height={'10px'}
          startColor={startColor}
          endColor={endColor}
          borderRadius={'12px'}
        />
      </div>
      <Skeleton
        width={'64px'}
        height={'64px'}
        startColor={startColor}
        endColor={endColor}
        borderRadius={'full'}
      />
    </div>
  );
};

const RemainingTime = ({ resetSwipeAt }) => {
  const [time, setTime] = useState({ hours: 0, minutes: 0 });

  useEffect(() => {
    const calculateTime = () => {
      const resetSwipeAtTime = new Date(resetSwipeAt);
      const now = new Date();
      const timeDiff = resetSwipeAtTime - now;

      const hours = Math.floor((timeDiff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
      const minutes = Math.floor((timeDiff % (1000 * 60 * 60)) / (1000 * 60));

      setTime({ hours, minutes });
    };

    calculateTime();
    const interval = setInterval(calculateTime, 1000 * 60); // Update every minute

    return () => clearInterval(interval);
  }, [resetSwipeAt]);

  return time;
}

const LastSwipedTimeDescription = ({ resetSwipeAt }) => {
  if (!resetSwipeAt) {
    return <></>;
  }

  const { hours, minutes } = RemainingTime({ resetSwipeAt });

  return (
    <Description>
      <Trans
        i18nKey="InviteFanBuddySwipeCard.description"
        components={{ bold: <span style={{ fontWeight: 700 }} /> }}
        values={{
          remainingRestoreHours: hours.toString().padStart(2, '0'),
          remainingRestoreMinutes: minutes.toString().padStart(2, '0')
        }}
      />
    </Description>
  );
};

const UpdateInvitationCodeSection = ({ resetSwipeAt }) => {
  const [invitationCode, setInvitationCode] = useState();
  const toast = useToast();

  const onClickDone = async () => {
    if (isEmpty(invitationCode)) return;
    const response = await UpdateInvitationCode({ invitationCode: invitationCode });
    if (response.ok) {
      window.location.reload(); // Refresh the page to re-render
    } else {
      const responseBody = await response.json();
      toast({
        duration: 1500,
        render: () => (
          <ToastMessageBox message={`Error: ${responseBody.error.message}`} showOnBottom={true} />
        )
      });
    }
  };

  return (
    <div className="justify-items-center mb-[100px]">
      <Title className="pb-[8px]">{'inputInvitationTitle'}</Title>
      <LastSwipedTimeDescription resetSwipeAt={resetSwipeAt} />
      <div className="w-full">
        <Input
          className="w-full mt-[21px]"
          borderRadius={'12px'}
          height={'46px'}
          bgColor={LightColors.purewhite}
          focusBorderColor={LightColors.purewhite}
          _hover={{
            border: '1px solid #00000030'
          }}
          border={'1px solid #00000030'}
          onChange={(e) => setInvitationCode(e.target.value)}
        />
      </div>
      <div className="self-center mt-[20px]">
        <PrimaryButton
          width={'fit-content'}
          fontWeight={600}
          fontSize={'14px'}
          lineHeight={'17px'}
          padding={'11px 18px'}
          text={'inputInvitationButton'}
          onClick={() => onClickDone()}
        />
      </div>
    </div>
  );
};

const NotAvailbleSwipeSection = ({ resetSwipeAt }) => {
  if (!resetSwipeAt) {
    return <></>;
  }

  return (
    <div className="justify-items-center mb-[100px]">
      <Title className="pb-[8px]">{t('InviteFanBuddySwipeCard.notAvailableSwipeTitle')}</Title>
      <LastSwipedTimeDescription resetSwipeAt={resetSwipeAt} />
    </div>
  );
};

// const CopyInvitationCodeSection = ({invitationCode, lastSwipedAt}) => {
//   const toast = useToast();

//   const onClickCopyInviteCode = () => {
//     copyTextToClipboard(invitationCode)
//       .then(() => {
//         toast({
//           duration: 1500,
//           render: () => (
//             <ToastMessageBox
//               message={t('InviteFanBuddySwipeCard.copiedToast')}
//               showOnBottom={true}
//             />
//           )
//         });
//       })
//       .catch(() => {
//         toast({
//           duration: 1500,
//           render: () => (
//             <ToastMessageBox
//               message={t('InviteFanBuddySwipeCard.failedToast')}
//               showOnBottom={true}
//             />
//           )
//         });
//       });
//   }

//   return (
//     <div className="justify-items-center mb-[100px]">
//       <Title className="pb-4">{t('InviteFanBuddySwipeCard.shareInvitationTitle')}</Title>
//       <LastSwipedTimeDescription lastSwipedAt={lastSwipedAt} />
//       <div className="self-center mt-[20px]">
//         <PrimaryButton
//           width={'fit-content'}
//           fontWeight={600}
//           fontSize={'14px'}
//           lineHeight={'17px'}
//           padding={'11px 18px'}
//           text={t('InviteFanBuddySwipeCard.shareInvitationButton')}
//           onClick={() => onClickCopyInviteCode()}
//         />
//       </div>
//     </div>
//   );
// }

// const InvitationDesclaimerSection = () => {
//   return (
//     <div>
//       <div className="flex flex-row">
//         <DotText className="pr-[8px]">{'•'}</DotText>
//         <Desclaimer>{t('InviteFanBuddySwipeCard.firstDesclaimer')}</Desclaimer>
//       </div>
//       <div className="flex flex-row">
//         <DotText className="pr-[8px]">{'•'}</DotText>
//         <Desclaimer>{t('InviteFanBuddySwipeCard.secondDesclaimer')}</Desclaimer>
//       </div>
//     </div>
//   );
// };
